<template>
  <input
    ref="input"
    type="file"
    :accept="accept"
    @input="handleFilesSelected"
  />
</template>

<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { formatBytes } from "@/Utils/size";

const { t } = useI18n();

const emit = defineEmits(["input"]);

const props = defineProps({
  accept: { type: String, default: undefined },
});

const input = ref(null);
const maxSize = 100 * 1024 * 1024; // 100 MB in bytes
const errors = ref([]);

function handleFilesSelected(e) {
  const files = Array.from(e.target.files);

  emit("input", filterValidFiles(files));

  // Allow selection of the same file again.
  e.target.value = null;
}

function filterValidFiles(files) {
  const unacceptedFiles = [];
  const oversizedFiles = [];
  const validFiles = [];
  const _errors = {};

  for (const file of files) {
    if (props.accept && !props.accept.includes(file.type)) {
      unacceptedFiles.push(file.name);
    } else if (file.size > maxSize) {
      oversizedFiles.push(file.name);
    } else {
      validFiles.push(file);
    }
  }

  if (unacceptedFiles.length > 0) {
    Object.assign(_errors, {
      file_type: t(
        "components.errors.files.upload.unaccepted_type_selected",
        {
          files: unacceptedFiles.join(", "),
        },
        unacceptedFiles.length
      ),
    });
  }

  if (oversizedFiles.length > 0) {
    Object.assign(_errors, {
      file_size: t(
        "components.errors.files.upload.size_limit_exceeded",
        {
          limit: formatBytes(maxSize),
          files: oversizedFiles.join(", "),
        },
        oversizedFiles.length
      ),
    });
  }

  errors.value = _errors;

  return validFiles;
}

defineExpose({
  errors,
  filterValidFiles,
  click: () => input.value?.click(),
});
</script>
